<script lang="ts">
	import type { Side, SearchField } from 'utility/search-fields'

	import Autocomplete from '@isoftdata/svelte-autocomplete'
	import EditModeControls from 'components/search/EditModeControls.svelte'
	import Pin from 'components/search/Pin.svelte'
	import { onMount, type ComponentProps } from 'svelte'
	import type { SvelteSet } from 'svelte/reactivity'
	import type { i18n } from 'types/common'
	import { getContext } from 'svelte'
	const { t: translate } = getContext<i18n>('i18next')

	interface SideOption {
		label: string
		value: Side
	}

	const sideList: SideOption[] = [
		{ label: translate('common:both', 'Both'), value: 'BOTH' },
		{ label: translate('common:left', 'Left'), value: 'LEFT' },
		{ label: translate('common:notSlashAcceptable', 'N/A'), value: 'NA' },
		{ label: translate('common:right', 'Right'), value: 'RIGHT' },
	]

	interface Props extends Omit<
		ComponentProps<typeof Autocomplete<SideOption | null>>,
		'value' | 'options' | 'emptyValue'
	> {
		searchField: SearchField
		inEditMode: boolean
		pinnedSearchFields: SvelteSet<SearchField['key']> //Required
		selectedSearchFieldsKeys: SvelteSet<SearchField['key']> //Required
		initialValue?: Side
		side?: SideOption | null
		disabled?: boolean
	}

	let {
		searchField,
		inEditMode,
		pinnedSearchFields = $bindable(),
		selectedSearchFieldsKeys = $bindable(),
		initialValue = undefined,
		side = $bindable(null),
		disabled = false,
		...rest
	}: Props = $props()

	onMount(() => {
		if (initialValue) {
			side = sideList.find(option => option.value === initialValue) ?? null
		}
	})
</script>

<EditModeControls
	{searchField}
	{inEditMode}
	{pinnedSearchFields}
	{selectedSearchFieldsKeys}
>
	<Autocomplete
		{...rest}
		label={translate('common:side', 'Side')}
		options={sideList}
		emptyValue={null}
		getLabel={option => option?.label ?? translate('common:any', 'Any')}
		disabled={disabled || inEditMode}
		bind:value={side}
	>
		{#snippet hint()}
			<Pin isPinned={pinnedSearchFields.has(searchField.key)} />
		{/snippet}
	</Autocomplete>
</EditModeControls>
